import React, { useState } from 'react';
import { Mail, Phone, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { brandsmartSendOtp } from '../../services/brandsmart.api';
import { useAuthStore } from '../../store/authStore';

interface Props {
  onNext: (contact: string, contactType: 'email' | 'phone') => void;
}

export default function LocateStep1Contact({ onNext }: Props) {
  const { t } = useTranslation('brandsmart');
  const { user } = useAuthStore();
  const [contactType, setContactType] = useState<'email' | 'phone'>('email');
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSend() {
    const trimmed = value.trim();
    if (!trimmed) return;

    // Validate format before API call
    if (contactType === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error(t('locateStep1.invalidEmail'));
      return;
    }
    if (contactType === 'phone') {
      const digits = trimmed.replace(/\D/g, '');
      if (digits.length < 7 || digits.length > 15) {
        toast.error(t('locateStep1.invalidPhone'));
        return;
      }
    }

    // Block if user enters their own email (already logged in with it)
    if (contactType === 'email' && user?.email && trimmed.toLowerCase() === user.email.toLowerCase()) {
      toast.error(t('locateStep1.sameEmailError'));
      return;
    }

    setLoading(true);
    try {
      await brandsmartSendOtp({ identifier: trimmed });
      onNext(trimmed, contactType);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('locateStep1.sendError');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-1">{t("locateStep1.heading")}</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {t("locateStep1.body")}
        </p>
      </div>

      {/* Tab switcher */}
      <div className="flex rounded-lg border overflow-hidden w-fit">
        <button
          onClick={() => { setContactType('email'); setValue(''); }}
          className={[
            'flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors',
            contactType === 'email'
              ? 'bg-primary text-primary-foreground'
              : 'bg-card text-muted-foreground hover:bg-muted',
          ].join(' ')}
        >
          <Mail className="h-4 w-4" />
          {t('locateStep1.email')}
        </button>
        <button
          onClick={() => { setContactType('phone'); setValue(''); }}
          className={[
            'flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors',
            contactType === 'phone'
              ? 'bg-primary text-primary-foreground'
              : 'bg-card text-muted-foreground hover:bg-muted',
          ].join(' ')}
        >
          <Phone className="h-4 w-4" />
          {t('locateStep1.phone')}
        </button>
      </div>

      {/* Input */}
      <div className="space-y-2">
        <label className="text-sm font-medium">
          {contactType === 'email' ? t('locateStep1.emailLabel') : t('locateStep1.phoneLabel')}
        </label>
        <input
          type={contactType === 'email' ? 'email' : 'tel'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          placeholder={
            contactType === 'email'
              ? t('locateStep1.emailPlaceholder')
              : t('locateStep1.phonePlaceholder')
          }
          autoFocus
          className="w-full border rounded-lg px-4 py-3 text-sm bg-background text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        {contactType === 'phone' && (
          <p className="text-xs text-muted-foreground">
            <span dangerouslySetInnerHTML={{ __html: t('locateStep1.demoHint') }} />
          </p>
        )}
      </div>

      <button
        onClick={handleSend}
        disabled={loading || !value.trim()}
        className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-3 text-sm font-medium disabled:opacity-50 transition-opacity"
      >
        {loading ? t('locateStep1.sending') : t('locateStep1.sendCode')}
        {!loading && <ArrowRight className="h-4 w-4" />}
      </button>
    </div>
  );
}
